import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button
} from "@/components/ui/button";
import { MobileNav } from '@/components/mobile-nav';
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/router";
import { ChatMessage, MessageRole } from "@/types";
import {
  generateResponse,
  generateGreetingFromImage,
} from "@/lib/openai-service";
import { supabase } from "@/lib/supabase";
import { useCredits, checkCredits } from "@/hooks/use-credits";
import { useAccountQuota } from "@/hooks/use-account-quota";
import { CreditAlertDialog } from "@/components/credit-alert-dialog";
import { Persona, defaultPersonaId, getPersonaById } from "@/lib/personas";
import { saveChatMessage } from "@/lib/chat-service";
import { useSavedAccounts } from "@/hooks/useSavedAccounts";
import { queryClient } from "@/lib/queryClient";
import { generateAndSaveAudio, getAudioUrlForMessage, playAudioFromUrl, stopAudioPlayback } from "@/lib/audio-storage";
import { isImageRequest, fetchInstagramPosts, selectAppropriatePost, PublicInstagramPost } from '@/lib/instagram-post-service';
import ReactMarkdown from 'react-markdown';

import ChatInput from "@/components/chat-input";
import ChatProfileImage from "@/components/chat-profile-image";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ArrowLeft, MessageCircle, Zap, Volume2, VolumeX, Headphones, EyeOff, Eye, AlertCircle, RotateCcw } from "lucide-react";
import { motion } from "framer-motion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ChatPageProps {
  username: string;
  initialProfile: any;
  onReset: () => void;
}

export function ChatPage({ username, initialProfile, onReset }: ChatPageProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { session } = useAuth();
  const { availableCredits, useCredit } = useCredits();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data: _quotaInfo, refetch: _refetchQuota } = useAccountQuota();
  const { data: savedAccounts = [] } = useSavedAccounts(session?.user?.id);
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    const saved = localStorage.getItem(`chat-${username}`);
    // console.log(`로컬 스토리지에서 채팅 로드 시도: ${username}`, saved ? "데이터 있음" : "데이터 없음"); // 디버깅 로그
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          // console.log(`기존 메시지 ${parsed.length}개 로드됨`); // 디버깅 로그
          return parsed;
        }
      } catch (e) {
        console.error("저장된 채팅 파싱 오류:", e);
      }
    }
    // console.log("저장된 채팅 없음, 빈 배열 반환 또는 초기 메시지 설정 시도"); // 디버깅 로그
    return []; // 초기에는 항상 빈 배열로 시작하고, 선톡 로직에서 추가
  });

  const [showCreditAlert, setShowCreditAlert] = useState(false);
  const [searchTerm] = useState(""); // Removed unused setter function
  const [isGenerating, setIsGenerating] = useState(false);
  const [isInitialGreetingGenerating, setIsInitialGreetingGenerating] = useState(false);
  const [ttsEnabled, setTTSEnabled] = useState(false);
  const [hideTextContent, setHideTextContent] = useState(false);
  const [autoPlay, setAutoPlay] = useState(false);
  const [currentPlayingMessageId, setCurrentPlayingMessageId] = useState<string | null>(null);
  const [playedMessageIds, setPlayedMessageIds] = useState<Set<string>>(new Set());
  // 채팅 초기화 대화상자 상태
  const [resetDialogOpen, setResetDialogOpen] = useState<boolean>(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentPersona, setCurrentPersona] = useState<Persona>(() => {
    const savedPersonaId = localStorage.getItem(`persona-${username}`) || defaultPersonaId;
    const persona = getPersonaById(savedPersonaId);
    return {
      ...persona,
      systemMessage: persona.systemMessage.replace(/{username}/g, username),
    };
  });

  const greetingGeneratedRef = useRef(false); // 선톡 생성 여부 추적
  const manualResetInProgressRef = useRef(false); // 수동 초기화 시 자동 인사말 생성 방지

  // 추가: 환경 묘사 안정화를 위한 상태
  const [stableEnvironments, setStableEnvironments] = useState<Record<string, {
    prefixIndex: number | null;
    usePrefix: boolean;
    specialEvent: boolean;
    eventIndex: number | null;
  }>>({});

  const [currentProfileImage, setCurrentProfileImage] = useState<string | undefined>(() => {
    // localStorage에서 저장된 이미지 URL 확인
    const savedImage = localStorage.getItem(`profile-image-${username}`);
    // 저장된 이미지가 있으면 사용, 없으면 기본 이미지 사용
    return savedImage || initialProfile?.profilePicUrlHD || initialProfile?.profilePicUrl;
  });

  // 환경 묘사 접두어 및 이벤트 생성 함수 (메시지 ID 기반)
  const getStableEnvironment = useCallback((messageId: string, content: string) => {
    if (!stableEnvironments[messageId]) {
      // 접두어 목록
      const prefixes = [
        '주변에서', '갑자기', '멀리서', '문득', '순간',
        '어디선가', '방 안에서', '바깥에서', '귓가에',
        '공기 중에', '주변 사람들이', '근처에서',
        '불현듯', '예상치 못하게', '점점', '희미하게'
      ];

      // 특별 이벤트 목록
      const events = [
        '누군가 문을 두드리는 소리가 들린다.',
        '갑자기 정전이 되었다.',
        '멀리서 사이렌 소리가 들려온다.',
        '당신의 전화기가 울린다.',
        '창밖에서 이상한 소리가 들린다.',
        '누군가 당신의 이름을 부르는 것 같다.',
        '주변 온도가 급격히 변하고 있다.',
        '당신은 누군가 쳐다보는 느낌을 받는다.'
      ];

      // 메시지 ID를 시드로 사용한 의사 난수 생성 (안정적인 값을 위해 Date.now() 제거)
      const generateHash = (str: string): number => {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
          const char = str.charCodeAt(i);
          hash = ((hash << 5) - hash) + char;
          hash = hash & hash; // 32비트 정수로 변환
        }
        return Math.abs(hash);
      };

      const messageHash = generateHash(messageId + content.substring(0, 20));
      const seededRandom = (salt = 1): number => {
        const x = Math.sin(messageHash * salt) * 10000;
        return Math.abs(x - Math.floor(x));
      };

      // 안정적인 환경 설정 생성
      const newEnvironment = {
        usePrefix: seededRandom(1) < 0.3, // 30% 확률로 접두어 사용
        prefixIndex: seededRandom(1) < 0.3 ? Math.floor(seededRandom(2) * prefixes.length) : null,
        specialEvent: seededRandom(3) < 0.08, // 8% 확률로 특별 이벤트 추가
        eventIndex: seededRandom(3) < 0.08 ? Math.floor(seededRandom(4) * events.length) : null
      };

      // 상태 업데이트
      setStableEnvironments(prev => ({
        ...prev,
        [messageId]: newEnvironment
      }));

      return newEnvironment;
    }

    return stableEnvironments[messageId];
  }, [stableEnvironments]);

  useEffect(() => {
    if (messages.length) {
      localStorage.setItem(`chat-${username}`, JSON.stringify(messages));
    }
  }, [messages, username]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // 메시지 상태 변경 감지 및 로깅
  useEffect(() => {
    console.log("메시지 상태 변경:", messages.length > 0 ? "메시지 있음" : "메시지 없음", messages.length);
    if (messages.length > 0) {
      console.log("첫 번째 메시지:", messages[0]?.role, messages[0]?.content?.substring(0, 30));
    }
  }, [messages]);

  useEffect(() => {
    if (!session?.user?.id) return;

    const channel = supabase
      .channel("saved_accounts_rt")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "saved_instagram_accounts" },
        () => {
          queryClient.invalidateQueries({
            queryKey: ["savedAccounts", session?.user?.id],
          });
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [session?.user?.id]);

  useEffect(() => {
    const account = savedAccounts.find((acc) => acc.username === username);
    if (!account?.persona_id) return;
    // DB에서 페르소나 조회
    (async () => {
      const { data, error } = await supabase
        .from("personas")
        .select("*")
        .eq("id", account.persona_id)
        .maybeSingle();

      if (error) {
        console.warn("DB에서 페르소나 조회 실패, 로컬 사용:", error.message);
        const fallback = getPersonaById(account.persona_id!);
        setCurrentPersona({
          ...fallback,
          systemMessage: fallback.systemMessage.replace(
            /{username}/g,
            username
          ),
        });
      } else if (data) {
        const fallback = getPersonaById(data.id);
        setCurrentPersona({
          id: data.id,
          name: data.name_ko,
          description: fallback.description,
          emoji: data.emoji,
          systemMessage: data.systemmessage.replace(/{username}/g, username),
        });
      }
    })();
  }, [savedAccounts, username]);

  // 자동 인사말 생성 로직 수정
  useEffect(() => {
    const generateInitialGreeting = async () => {
      // console.log("[Greeting Effect] Attempting to generate initial greeting."); // 디버깅 로그

      // 조건: 메시지가 없고, 수동 리셋 중이 아니며, 인사말이 아직 생성되지 않았을 때
      if (messages.length === 0 && !manualResetInProgressRef.current && !greetingGeneratedRef.current) {
        console.log(`[Greeting Effect] Conditions met for ${username}. Proceeding...`); // 디버깅 로그
        setIsInitialGreetingGenerating(true);
        greetingGeneratedRef.current = true; // 생성 시도 플래그 설정

        try {
          const latestPosts = initialProfile?.latestPosts;
          const firstPostImage = latestPosts?.[0]?.displayUrl;
          const savedAccount = savedAccounts.find((acc) => acc.username === username);
          const savedLatestPostUrl = savedAccount?.supabase_latest_post_url;
          const imageUrl = firstPostImage || savedLatestPostUrl || initialProfile?.profilePicUrlHD || initialProfile?.profilePicUrl;
          const userPersona = session?.user?.user_metadata?.persona || '';

          let greetingText: string;

          console.log(`[Greeting Effect] Calling generateGreetingFromImage for ${username}`); // 디버깅 로그
          greetingText = await generateGreetingFromImage(
            username,
            imageUrl || "",
            initialProfile,
            userPersona
          );

          if (!greetingText) { // generateGreetingFromImage가 빈 문자열을 반환할 경우 대비
            console.warn("[Greeting Effect] Received empty greeting text, using fallback.");
            const hardcodedGreetings = [
              `안녕! ${username}이야. 만나서 반가워.`, `뭐해?`, `안녕하세요 ㅎ`,
              `오늘 하루 어땠어?`, `잘지내지?`,
            ];
            greetingText = hardcodedGreetings[Math.floor(Math.random() * hardcodedGreetings.length)];
          }
          console.log(`[Greeting Effect] Greeting text for ${username}: "${greetingText}"`); // 디버깅 로그

          const greetingMessage: ChatMessage = {
            id: `greeting-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`,
            content: greetingText,
            role: MessageRole.Bot,
            timestamp: new Date().toISOString(),
            imageUrl: imageUrl || undefined,
          };

          setMessages([greetingMessage]); // 첫 메시지로 설정
          localStorage.setItem(`chat-${username}`, JSON.stringify([greetingMessage])); // 로컬 스토리지에도 저장

          try {
            await saveChatMessage(greetingMessage, username, currentPersona.id);
            // console.log("[Greeting Effect] Greeting saved to DB."); // 디버깅 로그
          } catch (e) {
            console.warn("[Greeting Effect] Failed to save initial greeting to DB:", e);
          }

        } catch (err) {
          console.error("[Greeting Effect] Failed to generate greeting:", err);
          greetingGeneratedRef.current = false; // 오류 발생 시 플래그 초기화하여 재시도 가능하게
        } finally {
          setIsInitialGreetingGenerating(false);
          // console.log("[Greeting Effect] Process finished."); // 디버깅 로그
        }
      } else {
        // console.log("[Greeting Effect] Conditions not met or greeting already generated."); // 디버깅 로그
        if (messages.length > 0 && !greetingGeneratedRef.current) {
          // console.log("[Greeting Effect] Messages loaded from storage, marking greeting as generated."); // 디버깅 로그
          greetingGeneratedRef.current = true;
        }
      }
    };

    if (username) {
      const timerId = setTimeout(generateInitialGreeting, 100);
      return () => clearTimeout(timerId);
    }
  }, [username, initialProfile, currentPersona.id, session, savedAccounts]);

  // Added: User interaction listener for mobile audio autoplay
  useEffect(() => {
    const handleUserInteraction = () => {
      const audioElements: HTMLCollectionOf<HTMLAudioElement> = document.getElementsByTagName('audio');
      for (const audio of Array.from(audioElements)) {
        if (audio.paused) {
          audio.play().catch(() => { });
        }
      }
    };

    window.addEventListener('click', handleUserInteraction);
    window.addEventListener('touchstart', handleUserInteraction);
    window.addEventListener('keydown', handleUserInteraction);

    return () => {
      window.removeEventListener('click', handleUserInteraction);
      window.removeEventListener('touchstart', handleUserInteraction);
      window.removeEventListener('keydown', handleUserInteraction);
    };
  }, []);

  // filter saved accounts by search term
  const filteredAccounts = savedAccounts.filter(
    (acc) =>
      acc.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      acc.profile_data?.fullName
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase())
  );

  const handleAccountSelect = (account: any) => {
    if (account.username === username) return;
    onReset();
    navigate("/", { state: { username: account.username } });
    setSidebarOpen(false);
  };

  // 특정 메시지 TTS 재생 처리
  const playMessageAudio = async (messageId: string, messageContent: string) => {
    // 이미 재생 중인 내용이 있으면 중지
    if (currentPlayingMessageId) {
      stopAudioPlayback();
      setCurrentPlayingMessageId(null);
    }

    // 현재 재생 메시지 ID 설정
    setCurrentPlayingMessageId(messageId);

    try {
      // 1. 먼저 기존 음성파일 URL이 있는지 확인
      const existingAudioUrl = await getAudioUrlForMessage(messageId);

      if (existingAudioUrl) {
        console.log(`메시지 ID ${messageId}의 기존 오디오 파일 발견:`, existingAudioUrl);

        // 기존 파일 재생
        const success = await playAudioFromUrl(existingAudioUrl);
        if (success) {
          // 재생 완료 처리
          setPlayedMessageIds((prev) => new Set(prev).add(messageId));
          setCurrentPlayingMessageId(null);
        } else {
          console.log('저장된 음성파일 재생 실패, 새로 생성합니다.');
          // 재생 실패 시 새로 생성
          await generateAndSaveAudio(
            messageContent,
            messageId,
            "j9qX8lQT2vsTocP0jI9L",
            () => {
              setPlayedMessageIds((prev) => new Set(prev).add(messageId));
              setCurrentPlayingMessageId(null);
            },
            (error) => {
              console.error('음성 생성 오류:', error);
              setCurrentPlayingMessageId(null);
              toast({
                title: "오류",
                description: "음성 파일을 생성하는데 실패했습니다.",
                variant: "destructive",
              });
            }
          );
        }
      } else {
        // 저장된 음성이 없으면 새로 생성 및 저장
        await generateAndSaveAudio(
          messageContent,
          messageId,
          "j9qX8lQT2vsTocP0jI9L",
          () => {
            setPlayedMessageIds((prev) => new Set(prev).add(messageId));
            setCurrentPlayingMessageId(null);
          },
          (error) => {
            console.error('음성 생성 오류:', error);
            setCurrentPlayingMessageId(null);
            toast({
              title: "오류",
              description: "음성 파일을 생성하는데 실패했습니다.",
              variant: "destructive",
            });
          }
        );
      }
    } catch (error) {
      console.error('음성 처리 중 예외 발생:', error);
      setCurrentPlayingMessageId(null);
      toast({
        title: "오류",
        description: "음성 파일 처리 중 문제가 발생했습니다.",
        variant: "destructive",
      });
    }
  };

  const handleSendMessage = async (text: string) => {
    console.log("[ChatPage] handleSendMessage called with:", text);
    if (isGenerating) {
      console.log("[ChatPage] Dropped: still generating");
      return;
    }
    if (!text.trim()) {
      console.log("[ChatPage] Dropped: empty text");
      return;
    }

    // 크레딧 체크
    const hasEnoughCredits = await checkCredits(1);
    if (!hasEnoughCredits) {
      console.log("[ChatPage] Not enough credits");
      setShowCreditAlert(true);
      return;
    }

    // 생성 시작
    setIsGenerating(true);
    console.log("[ChatPage] Starting message generation...");

    // 사용자 메시지 생성
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      content: text,
      role: MessageRole.User,
      timestamp: new Date().toISOString(),
    };

    // 상태 업데이트 및 UI에 사용자 메시지 표시
    setMessages((prev) => [...prev, userMessage]);
    console.log("[ChatPage] Added user message to state:", userMessage.id);

    // 사용자 메시지 ID - 이미 생성
    const userMessageId = userMessage.id;
    // 봇 메시지 ID는 userMessageId + 1로 생성
    const botMessageId = (parseInt(userMessageId) + 1).toString();

    try {
      // 크레딧 사용
      const { success } = await useCredit(1, `대화: @${username}`);
      if (!success) {
        throw new Error("크레딧 사용에 실패했습니다");
      }
      console.log("[ChatPage] Credit used successfully");

      // 사용자 메시지 DB 저장
      await saveChatMessage(userMessage, username, currentPersona.id);
      console.log("[ChatPage] Saved user message to DB");

      // 채팅 히스토리 준비
      const chatHistory = [...messages, userMessage];

      // 사용자 페르소나 정보 가져오기
      const userPersona = session?.user?.user_metadata?.persona || '';
      console.log("[ChatPage] Using persona:", userPersona ? "Yes" : "No");

      // 현재 재생 중인 TTS가 있으면 중지
      if (currentPlayingMessageId) {
        stopAudioPlayback();
        setCurrentPlayingMessageId(null);
      }

      // 빈 봇 메시지를 미리 추가하여 로딩 상태 표시
      setMessages((prev) => [
        ...prev,
        {
          id: botMessageId,
          content: "",
          role: MessageRole.Bot,
          timestamp: new Date().toISOString(),
        },
      ]);
      console.log("[ChatPage] Added empty bot message for loading state:", botMessageId);

      // 사진 요청인지 확인
      const isPhotoRequest = isImageRequest(text);
      console.log("[ChatPage] 사진 요청 여부:", isPhotoRequest);

      // 사진 요청이면 인스타그램 게시물 가져오기
      let selectedPost: PublicInstagramPost | null = null;
      let imageUrl: string | null = null;

      if (isPhotoRequest) {
        console.log("[ChatPage] 사진 요청 감지됨, 인스타그램 게시물 조회 중...");
        try {
          const posts = await fetchInstagramPosts(username);

          if (posts.length > 0) {
            selectedPost = selectAppropriatePost(posts, text);
            if (selectedPost) {
              imageUrl = selectedPost.imagekit_url;
              console.log("[ChatPage] 선택된 게시물:", selectedPost.caption?.substring(0, 30));
              if (imageUrl) {
                console.log("[ChatPage] 이미지 URL:", imageUrl.substring(0, 30) + "...");
              }
            }
          } else {
            console.log("[ChatPage] 게시물을 찾을 수 없음");
          }
        } catch (error) {
          console.error("[ChatPage] 게시물 조회 오류:", error);
        }
      }

      // 일반 대화 응답 생성 (userPersona 전달)
      console.log("[ChatPage] Generating response...");
      let botResponse = "";

      // 이미지 요청에 대한 특별 응답 생성
      if (isPhotoRequest) {
        let additionalContext = "";

        if (selectedPost && imageUrl) {
          // 이미지가 있는 경우, "지금 찍은 사진"처럼 이야기하도록 지시
          additionalContext = `사용자가 사진을 요청했고, 내가 지금 찍은 사진을 보여줄거야. 이 사진에 대해 대화하자. 사진 캡션을 직접 언급하지 말고, 마치 이 사진을 지금 찍은 것처럼 가장해서 대화해. 너무 디테일하게 묘사하지 말고 자연스럽게 얘기해. 참고로 이 사진의 원래 정보는: ${selectedPost.caption || '설명 없음'} (이 정보는 너의 답변에 직접 쓰지 마!)`;

          // 이미지와 함께 응답 생성
          botResponse = await generateResponse(
            username,
            text,
            chatHistory,
            initialProfile,
            currentPersona.systemMessage + "\n\n" + additionalContext,
            (chunk: string) => {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === botMessageId ? { ...msg, content: msg.content + chunk } : msg
                )
              );
            },
            userPersona // 사용자 페르소나 전달
          );
        } else {
          // 이미지가 없는 경우 이미지가 없다는 메시지 추가
          additionalContext = "사용자가 사진을 요청했지만, 나는 게시물이 없어. 사진이 없는 이유를 자연스럽게 설명하고 대화를 이어가자.";

          botResponse = await generateResponse(
            username,
            text,
            chatHistory,
            initialProfile,
            currentPersona.systemMessage + "\n\n" + additionalContext,
            (chunk: string) => {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === botMessageId ? { ...msg, content: msg.content + chunk } : msg
                )
              );
            },
            userPersona // 사용자 페르소나 전달
          );
        }
      } else {
        // 일반 대화 응답 생성
        botResponse = await generateResponse(
          username,
          text,
          chatHistory,
          initialProfile,
          currentPersona.systemMessage,
          (chunk: string) => {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === botMessageId ? { ...msg, content: msg.content + chunk } : msg
              )
            );
          },
          userPersona // 사용자 페르소나 전달
        );
      }
      console.log("[ChatPage] Response generated, length:", botResponse.length);

      // 최종 봇 메시지 객체
      const finalBotMessage: ChatMessage = {
        id: botMessageId,
        content: botResponse, // 전체 응답 내용
        role: MessageRole.Bot,
        timestamp: new Date().toISOString(),
        imageUrl: imageUrl, // 이미지 URL이 있으면 추가
      };

      // 최종 메시지로 업데이트
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === botMessageId ? finalBotMessage : msg
        )
      );
      console.log("[ChatPage] Updated bot message with final content");

      // DB에 최종 메시지 저장
      await saveChatMessage(finalBotMessage, username, currentPersona.id);
      console.log("[ChatPage] Saved bot message to DB");

      // 자동 재생 활성화되어 있고 TTS 가 활성화되어 있으면
      if (autoPlay && ttsEnabled) {
        console.log("[ChatPage] Auto-playing TTS");
        playMessageAudio(botMessageId, botResponse);
      }

    } catch (error) {
      console.error("[ChatPage] Error generating response:", error);
      toast({
        title: "오류",
        description: "응답을 생성하는데 실패했습니다. 다시 시도해주세요.",
        variant: "destructive",
      });

      // 에러 발생 시 빈 봇 메시지 제거
      setMessages((prev) => prev.filter((msg) => msg.id !== botMessageId));
      console.log("[ChatPage] Removed empty bot message due to error");
    } finally {
      setIsGenerating(false);
      console.log("[ChatPage] Generation process completed");
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M";
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + "K";
    }
    return num.toString();
  };

  // 프로필 이미지 변경 핸들러
  const handleProfileImageChange = (newImageUrl: string): void => {
    setCurrentProfileImage(newImageUrl);

    // localStorage에 선택한 이미지 URL 저장
    localStorage.setItem(`profile-image-${username}`, newImageUrl);

    console.log('[ChatPage] 프로필 이미지 변경됨:', newImageUrl);
  };

  return (
    <div className="flex h-screen flex-col bg-gradient-to-b from-background via-background/95 to-background/90">
      <div className="absolute inset-0 bg-grid-pattern opacity-20"></div>

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-40 w-72 transform border-r bg-background/95 backdrop-blur-lg transition-transform duration-300 sm:hidden ${sidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
      >
        <div className="flex h-16 items-center justify-between border-b px-4">
          <h2 className="gradient-heading text-lg font-semibold">
            저장된 계정
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(false)}
            className="sm:hidden"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </div>
        <div className="h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="p-4">
            <Button
              variant="outline"
              className="w-full justify-start gap-2 group transition-all duration-300"
              onClick={() => {
                navigate("/chat");
                setSidebarOpen(false);
              }}
            >
              <div className="rounded-full bg-primary/10 p-2 group-hover:bg-primary/20 transition-colors duration-300">
                <ArrowLeft className="h-3 w-3 text-primary" />
              </div>
              <span>채팅 목록으로 돌아가기</span>
            </Button>
            <div className="mt-4 flex items-center gap-2">
              <Zap className="h-4 w-4 text-primary" />
              <span className="text-sm">
                {availableCredits ?? 0} 크레딧 보유
              </span>
            </div>

            {savedAccounts.length > 0 && (
              <>
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-white/10" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background/80 px-2 text-muted-foreground backdrop-blur-sm">
                      저장된 계정
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  {filteredAccounts.map((account) => (
                    <Button
                      key={account.username}
                      variant="ghost"
                      className={`w-full justify-start gap-3 transition-all h-auto py-3 ${account.username === username
                        ? "bg-primary/10 text-primary"
                        : "hover:bg-muted/50"
                        }`}
                      onClick={() => handleAccountSelect(account)}
                    >
                      <Avatar className="h-7 w-7 ring-2 ring-primary/10">
                        <AvatarImage
                          src={account.profile_data?.profilePicUrl}
                          alt={account.username}
                        />
                        <AvatarFallback className="bg-gradient-to-br from-primary/40 to-accent/40 text-white">
                          {account.username.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col items-start text-sm">
                        <span className="font-medium">@{account.username}</span>
                        {account.profile_data?.fullName && (
                          <span className="text-xs text-muted-foreground">
                            {account.profile_data.fullName}
                          </span>
                        )}
                        {account.profile_data?.followersCount !== undefined && (
                          <span className="text-xs text-muted-foreground">
                            팔로워{" "}
                            {formatNumber(account.profile_data.followersCount)}
                            명
                          </span>
                        )}
                        {account.profile_data?.biography && (
                          <span className="text-xs text-muted-foreground truncate max-w-[180px]">
                            {account.profile_data.biography}
                          </span>
                        )}
                      </div>
                    </Button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Header - Fixed position for mobile keyboard */}
      <header className="fixed top-0 left-0 right-0 z-30 border-b border-gray-200 dark:border-gray-700 bg-background/30 backdrop-blur-lg">
        <div className="container flex h-20 max-w-4xl items-center justify-between pl-4 pr-4 sm:pl-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="mr-2 hidden sm:flex"
              onClick={onReset}
            >
              <ArrowLeft className="h-5 w-5" />
              <span className="sr-only">뒤로</span>
            </Button>

            {/* 기존 Avatar 컴포넌트를 ChatProfileImage로 교체 */}
            <ChatProfileImage
              username={username}
              src={currentProfileImage}
              size="md"
              className="ring-2 ring-primary/10"
              onImageChange={handleProfileImageChange}
              showImageIcon={true}
            />

            <div className="flex flex-col">
              <div className="flex items-center gap-1">
                <span className="font-medium">@{username}</span>
                {initialProfile?.isVerified && (
                  <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-blue-500 text-[10px] text-white">
                    ✓
                  </span>
                )}
              </div>
              {initialProfile?.followersCount && (
                <span className="text-xs text-muted-foreground">
                  팔로워 {formatNumber(initialProfile.followersCount)}명
                </span>
              )}
              {initialProfile?.biography && (
                <span className="text-xs text-muted-foreground truncate max-w-[180px]">
                  {initialProfile.biography}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* 채팅 초기화 버튼 */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setResetDialogOpen(true)}
              className="relative"
              title="채팅 초기화"
            >
              <RotateCcw className="h-4 w-4" />
              <span className="sr-only">채팅 초기화</span>
            </Button>

            {/* 음성 모드 토글 버튼 */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                const newState = !ttsEnabled;
                setTTSEnabled(newState);
                if (!newState && currentPlayingMessageId) {
                  stopAudioPlayback();
                  setCurrentPlayingMessageId(null);
                }
                if (newState) {
                  toast({
                    title: "음성 모드 활성화",
                    description: "이제 메시지가 음성으로 재생됩니다.",
                  });
                }
              }}
              className="relative"
              title={ttsEnabled ? "음성 끄기" : "음성 켜기"}
            >
              {ttsEnabled ? (
                <Volume2 className="h-4 w-4 text-primary" />
              ) : (
                <VolumeX className="h-4 w-4" />
              )}
              <span className="sr-only">
                {ttsEnabled ? "음성 끄기" : "음성 켜기"}
              </span>
              {ttsEnabled && currentPlayingMessageId && (
                <span className="absolute top-0 right-0 h-2 w-2 rounded-full bg-green-500 animate-pulse"></span>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Chat messages area */}
      <main className="relative flex-1 overflow-auto min-h-0 py-4 pb-36 pt-24 md:pb-20">
        <div className="container mx-auto max-w-4xl flex flex-col h-auto min-h-full justify-end md:justify-end gap-y-8">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full space-y-6 text-center">
              <div className="rounded-full bg-gradient-to-br from-primary/20 to-accent/20 p-6 shadow-xl animate-float">
                <MessageCircle className="h-12 w-12 text-primary" />
              </div>
              <div className="space-y-3 max-w-sm">
                <h3 className="gradient-heading text-2xl font-semibold">
                  채팅을 시작해 보세요
                </h3>
                {isInitialGreetingGenerating && (
                  <p className="text-sm text-muted-foreground">
                    첫 인사말을 생성하는 중입니다...
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-8 flex-grow">
              {messages.map((message, index) => (
                <div
                  key={message.id || `msg-${index}`}
                  className={`group animate-fade-in transition-opacity ${message.role === MessageRole.User
                    ? "flex justify-end w-full"
                    : message.role === MessageRole.System
                      ? "mx-auto"
                      : ""
                    }`}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{
                      duration: 0.3,
                      delay: index > messages.length - 3 ? 0.1 * (index % 3) : 0
                    }}
                    className={`flex items-start gap-5 ${message.role === MessageRole.User
                      ? "flex-row-reverse"
                      : message.role === MessageRole.System
                        ? "mx-auto"
                        : ""
                      }`}
                  >
                    {message.role === MessageRole.Bot && (
                      <div className="relative flex-shrink-0">
                        <div className="relative">
                          <motion.div
                            initial={{ scale: 0.8 }}
                            animate={{ scale: 1 }}
                            transition={{ duration: 0.2 }}
                          >
                            <ChatProfileImage
                              username={username}
                              src={currentProfileImage}
                              size="sm"
                              className="mt-2 ring-2 ring-primary/5 shadow-sm"
                              showImageIcon={false}
                            />
                          </motion.div>
                          {isGenerating && messages[messages.length - 1].id === message.id && (
                            <motion.div
                              className="absolute -bottom-1 -right-1 h-3 w-3 rounded-full bg-primary"
                              animate={{ scale: [1, 1.2, 1] }}
                              transition={{ repeat: Infinity, duration: 1.5 }}
                            />
                          )}
                        </div>
                      </div>
                    )}

                    {message.role === MessageRole.User && (
                      <div className="relative flex-shrink-0 ml-3">
                        <motion.div
                          initial={{ scale: 0.8 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 0.2 }}
                        >
                          <Avatar className="h-12 w-12 ring-2 ring-primary/10 shadow-sm">
                            <AvatarImage src={session?.user?.user_metadata?.avatar_url || undefined} alt="User Avatar" />
                            <AvatarFallback className="bg-gradient-to-br from-accent to-primary text-white">
                              나
                            </AvatarFallback>
                          </Avatar>
                        </motion.div>
                      </div>
                    )}

                    <div className={`flex flex-col gap-1 flex-1 ${message.role === MessageRole.User ? "items-end" : ""}`}>
                      {message.role === MessageRole.System ? (
                        <div className="rounded-lg bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                          {message.content}
                        </div>
                      ) : (
                        <div className={`flex ${message.role === MessageRole.User ? 'justify-end w-full' : ''}`}>
                          <motion.div
                            whileHover={{ scale: 1.01 }}
                            transition={{ type: "spring", stiffness: 400, damping: 25 }}
                            className={`rounded-xl shadow-sm relative overflow-hidden inline-block ${message.role === MessageRole.Bot
                              ? "chat-bubble-bot bg-card/80 backdrop-blur-sm border border-slate-200 dark:border-slate-700 p-3.5 text-sm leading-relaxed whitespace-pre-wrap break-words"
                              : "chat-bubble-user border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm px-4 py-3 text-sm"
                              }`}
                          >
                            {/* 배경 효과 */}
                            {message.role === MessageRole.Bot && (
                              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5 opacity-30" />
                            )}

                            {/* 메시지 내용 컨테이너 */}
                            <div className={`relative ${message.role === MessageRole.User ? "" : ""}`}>
                              {message.role === MessageRole.Bot && isGenerating && message.content === "" ? (
                                <div className="flex items-center space-x-2">
                                  <div className="flex space-x-1">
                                    <motion.div
                                      className="h-2 w-2 rounded-full bg-primary/40"
                                      animate={{
                                        y: [0, -4, 0],
                                        opacity: [0.4, 1, 0.4]
                                      }}
                                      transition={{
                                        repeat: Infinity,
                                        duration: 1,
                                        delay: 0
                                      }}
                                    />
                                    <motion.div
                                      className="h-2 w-2 rounded-full bg-primary/40"
                                      animate={{
                                        y: [0, -4, 0],
                                        opacity: [0.4, 1, 0.4]
                                      }}
                                      transition={{
                                        repeat: Infinity,
                                        duration: 1,
                                        delay: 0.3
                                      }}
                                    />
                                    <motion.div
                                      className="h-2 w-2 rounded-full bg-primary/40"
                                      animate={{
                                        y: [0, -4, 0],
                                        opacity: [0.4, 1, 0.4]
                                      }}
                                      transition={{
                                        repeat: Infinity,
                                        duration: 1,
                                        delay: 0.6
                                      }}
                                    />
                                  </div>
                                  <p className="text-sm text-muted-foreground">메시지를 생성하는 중...</p>
                                </div>
                              ) : isGenerating && messages[messages.length - 1].id === message.id && message.content !== "" ? (
                                <div className="relative break-words">
                                  <div className={`${message.role === MessageRole.User
                                    ? "text-foreground leading-relaxed"
                                    : "text-sm leading-relaxed whitespace-pre-wrap break-words markdown-content"
                                    }`}>
                                    <ReactMarkdown
                                      components={{
                                        p: ({ children }) => <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>,
                                        em: ({ children }) => {
                                          const content = String(children);

                                          // 메시지 ID 기반으로 일관된 환경 정보 가져오기
                                          const env = getStableEnvironment(message.id, content);

                                          // 접두어 목록
                                          const prefixes = [
                                            '주변에서', '갑자기', '멀리서', '문득', '순간',
                                            '어디선가', '방 안에서', '바깥에서', '귓가에',
                                            '공기 중에', '주변 사람들이', '근처에서',
                                            '불현듯', '예상치 못하게', '점점', '희미하게'
                                          ];

                                          // 특별 이벤트 목록
                                          const events = [
                                            '누군가 문을 두드리는 소리가 들린다.',
                                            '갑자기 정전이 되었다.',
                                            '멀리서 사이렌 소리가 들려온다.',
                                            '당신의 전화기가 울린다.',
                                            '창밖에서 이상한 소리가 들린다.',
                                            '누군가 당신의 이름을 부르는 것 같다.',
                                            '주변 온도가 급격히 변하고 있다.',
                                            '당신은 누군가 쳐다보는 느낌을 받는다.'
                                          ];

                                          // 메시지 ID를 기반으로 일관된 값 결정
                                          const usePrefix = env.usePrefix;
                                          const prefix = env.prefixIndex !== null ? prefixes[env.prefixIndex] : '';
                                          const specialEvent = env.specialEvent;
                                          const eventText = env.eventIndex !== null ? events[env.eventIndex] : '';

                                          // 감정 분석하여 클래스 결정 (이 부분은 동일하게 유지)
                                          let moodClass = '';
                                          const keywords = {
                                            danger: ['위험', '무서', '두렵', '공포', '비명', '경고', '조심', '주의', '긴장', '불안', '걱정', '경계', '죽음', '죽'],
                                            warning: ['소리', '울리', '울음', '외침', '고함', '시끄럽', '화', '분노', '화가', '짜증', '귀찮'],
                                            info: ['조용', '침묵', '고요', '평화', '차분', '평온', '고요', '조용', '안정', '신비', '궁금'],
                                            success: ['웃음', '미소', '행복', '기쁨', '즐거움', '설렘', '희망', '기대', '좋은', '편안']
                                          };

                                          for (const [mood, terms] of Object.entries(keywords)) {
                                            if (terms.some(term => content.includes(term))) {
                                              moodClass = `mood-${mood}`;
                                              break;
                                            }
                                          }

                                          // 최종 표시할 텍스트 구성
                                          const finalContent = usePrefix ? `${prefix} ${content}` : content;
                                          const finalText = specialEvent ? `${finalContent} ${eventText}` : finalContent;

                                          return (
                                            <em className={`italic text-slate-600 dark:text-slate-400 ${moodClass} transition-colors duration-200`}>
                                              {finalText}
                                            </em>
                                          );
                                        },
                                        strong: ({ children }) => <strong className="font-semibold text-slate-800 dark:text-slate-100 transition-colors duration-200">{children}</strong>,
                                      }}
                                    >
                                      {message.content}
                                    </ReactMarkdown>
                                  </div>
                                  <motion.div
                                    className="absolute bottom-0 right-0 mt-1 mr-1"
                                    animate={{ opacity: [0.5, 1, 0.5] }}
                                    transition={{ repeat: Infinity, duration: 2 }}
                                  >
                                    <span className="inline-flex items-center rounded-full bg-primary/20 px-2 py-1 text-xs text-primary">
                                      <span className="mr-1 h-1.5 w-1.5 animate-pulse rounded-full bg-primary"></span>
                                      계속 응답 중...
                                    </span>
                                  </motion.div>
                                </div>
                              ) : (
                                <>
                                  {/* 음성 아이콘 및 이미지+본문 */}
                                  {message.role === MessageRole.Bot && ttsEnabled && (
                                    <motion.div
                                      initial={{ opacity: 0, scale: 0.5 }}
                                      animate={{ opacity: 1, scale: 1 }}
                                      className="absolute -top-1.5 -right-1.5 z-10"
                                    >
                                      <Button
                                        variant="secondary"
                                        size="icon"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (currentPlayingMessageId === message.id) {
                                            stopAudioPlayback();
                                            setCurrentPlayingMessageId(null);
                                          } else {
                                            playMessageAudio(message.id, message.content);
                                          }
                                        }}
                                        className="h-8 w-8 rounded-full bg-background/90 backdrop-blur-sm shadow-md border border-primary/10 hover:bg-background/95"
                                      >
                                        {currentPlayingMessageId === message.id ? (
                                          <motion.div
                                            animate={{ scale: [1, 1.2, 1] }}
                                            transition={{ repeat: Infinity, duration: 1.5 }}
                                          >
                                            <VolumeX className="h-4 w-4 text-primary" />
                                          </motion.div>
                                        ) : (
                                          <Volume2 className={`h-4 w-4 ${playedMessageIds.has(message.id) ? 'text-primary' : 'text-muted-foreground'}`} />
                                        )}
                                      </Button>
                                    </motion.div>
                                  )}

                                  {/* 이미지 표시 부분 */}
                                  {message.imageUrl && (
                                    <motion.div
                                      initial={{ opacity: 0, y: 5 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      className="relative mb-3 overflow-hidden rounded-lg max-w-full"
                                    >
                                      <div className="mobile-responsive-image-container">
                                        <img
                                          src={message.imageUrl}
                                          alt="프로필 관련 이미지"
                                          className="w-full object-contain bg-black/5 dark:bg-black/20 mobile-responsive-image"
                                          onError={(e) => {
                                            (e.target as HTMLImageElement).src = "/default-post.png";
                                          }}
                                        />
                                      </div>
                                    </motion.div>
                                  )}

                                  {/* 텍스트 내용 부분 */}
                                  {(message.role === MessageRole.Bot && hideTextContent && ttsEnabled) ? (
                                    <motion.div
                                      initial={{ opacity: 0 }}
                                      animate={{ opacity: 1 }}
                                      className="flex items-center justify-center py-4 text-muted-foreground gap-2"
                                    >
                                      <Headphones className="h-4 w-4" />
                                      <span>음성으로 듣기</span>
                                    </motion.div>
                                  ) : (
                                    <div className={`break-words ${message.role === MessageRole.User
                                      ? "text-foreground leading-relaxed"
                                      : "text-sm leading-relaxed whitespace-pre-wrap break-words markdown-content"
                                      }`}>
                                      <ReactMarkdown
                                        components={{
                                          p: ({ children }) => <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>,
                                          em: ({ children }) => {
                                            const content = String(children);

                                            // 메시지 ID 기반으로 일관된 환경 정보 가져오기
                                            const env = getStableEnvironment(message.id, content);

                                            // 접두어 목록
                                            const prefixes = [
                                              '주변에서', '갑자기', '멀리서', '문득', '순간',
                                              '어디선가', '방 안에서', '바깥에서', '귓가에',
                                              '공기 중에', '주변 사람들이', '근처에서',
                                              '불현듯', '예상치 못하게', '점점', '희미하게'
                                            ];

                                            // 특별 이벤트 목록
                                            const events = [
                                              '누군가 문을 두드리는 소리가 들린다.',
                                              '갑자기 정전이 되었다.',
                                              '멀리서 사이렌 소리가 들려온다.',
                                              '당신의 전화기가 울린다.',
                                              '창밖에서 이상한 소리가 들린다.',
                                              '누군가 당신의 이름을 부르는 것 같다.',
                                              '주변 온도가 급격히 변하고 있다.',
                                              '당신은 누군가 쳐다보는 느낌을 받는다.'
                                            ];

                                            // 메시지 ID를 기반으로 일관된 값 결정
                                            const usePrefix = env.usePrefix;
                                            const prefix = env.prefixIndex !== null ? prefixes[env.prefixIndex] : '';
                                            const specialEvent = env.specialEvent;
                                            const eventText = env.eventIndex !== null ? events[env.eventIndex] : '';

                                            // 감정 분석하여 클래스 결정 (이 부분은 동일하게 유지)
                                            let moodClass = '';
                                            const keywords = {
                                              danger: ['위험', '무서', '두렵', '공포', '비명', '경고', '조심', '주의', '긴장', '불안', '걱정', '경계', '죽음', '죽'],
                                              warning: ['소리', '울리', '울음', '외침', '고함', '시끄럽', '화', '분노', '화가', '짜증', '귀찮'],
                                              info: ['조용', '침묵', '고요', '평화', '차분', '평온', '고요', '조용', '안정', '신비', '궁금'],
                                              success: ['웃음', '미소', '행복', '기쁨', '즐거움', '설렘', '희망', '기대', '좋은', '편안']
                                            };

                                            for (const [mood, terms] of Object.entries(keywords)) {
                                              if (terms.some(term => content.includes(term))) {
                                                moodClass = `mood-${mood}`;
                                                break;
                                              }
                                            }

                                            // 최종 표시할 텍스트 구성
                                            const finalContent = usePrefix ? `${prefix} ${content}` : content;
                                            const finalText = specialEvent ? `${finalContent} ${eventText}` : finalContent;

                                            return (
                                              <em className={`italic text-slate-600 dark:text-slate-400 ${moodClass} transition-colors duration-200`}>
                                                {finalText}
                                              </em>
                                            );
                                          },
                                          strong: ({ children }) => <strong className="font-semibold text-slate-800 dark:text-slate-100 transition-colors duration-200">{children}</strong>,
                                        }}
                                      >
                                        {message.content}
                                      </ReactMarkdown>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>

                            {/* 사용자 메시지의 경우 체크 표시 */}
                            {message.role === MessageRole.User && (
                              <motion.div
                                initial={{ opacity: 0, scale: 0 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.3 }}
                                className="absolute -top-1 -right-1 h-4 w-4 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center shadow-sm"
                              >
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                  className="text-primary" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M20 6L9 17l-5-5" />
                                </svg>
                              </motion.div>
                            )}
                          </motion.div>
                        </div>
                      )}

                      {/* 메시지 시간 표시 */}
                      {message.role !== MessageRole.System && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 0 }}
                          whileHover={{ opacity: 1 }}
                          className={`flex ${message.role === MessageRole.User ? 'justify-end pr-1' : 'justify-start pl-1'}`}
                        >
                          <span className="text-[10px] text-muted-foreground px-1 py-0.5 rounded bg-muted/30 backdrop-blur-sm">
                            {new Date(message.timestamp).toLocaleTimeString("ko-KR", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </motion.div>
                      )}
                    </div>
                  </motion.div>
                </div>
              ))}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </main>

      {/* Message input area fixed at the bottom */}
      <div className="fixed bottom-0 left-0 right-0 z-20 border-t bg-background/95 p-4 backdrop-blur-md pb-20 md:pb-4">
        <div className="container mx-auto max-w-4xl">
          {(isGenerating || isInitialGreetingGenerating) && (
            <div className="mb-2 text-center text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <span className="relative">
                  <span className="absolute -left-1.5 -top-1.5 animate-ping h-2 w-2 rounded-full bg-primary/70"></span>
                  <span className="h-2 w-2 rounded-full bg-primary/80"></span>
                </span>
                상대방이 답장을 보내고 있어요
              </span>
              <span className="animate-ping ml-1">.</span>
              <span
                className="animate-ping ml-1"
                style={{ animationDelay: "200ms" }}
              >
                .
              </span>
              <span
                className="animate-ping ml-1"
                style={{ animationDelay: "400ms" }}
              >
                .
              </span>
            </div>
          )}

          {/* 모바일: 음성 모드 안내 배너 (TTS가 꺼져있을 때만 표시) */}


          {/* TTS가 활성화 되었을 때 컨트롤 패널 */}
          {ttsEnabled && (
            <div className="mb-2 rounded bg-primary/10 p-2 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs flex items-center gap-1.5 rounded-full"
                  onClick={() => setHideTextContent(!hideTextContent)}
                >
                  {hideTextContent ? (
                    <>
                      <Eye className="h-3.5 w-3.5" />
                      <span>텍스트 표시</span>
                    </>
                  ) : (
                    <>
                      <EyeOff className="h-3.5 w-3.5" />
                      <span>텍스트 숨기기</span>
                    </>
                  )}
                </Button>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant={autoPlay ? "default" : "outline"}
                  size="sm"
                  className={`h-8 text-xs flex items-center gap-1.5 rounded-full ${autoPlay ? 'bg-primary/90' : ''}`}
                  onClick={() => {
                    setAutoPlay(!autoPlay);

                    toast({
                      title: !autoPlay ? "자동 재생 켜짐" : "자동 재생 꺼짐",
                      description: !autoPlay ? "새 메시지가 자동으로 음성 재생됩니다." : "음성을 수동으로 재생합니다.",
                    });
                  }}
                >
                  <Zap className="h-3.5 w-3.5" />
                  <span>자동 재생 {autoPlay ? "켜짐" : "꺼짐"}</span>
                </Button>

                {currentPlayingMessageId && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs rounded-full text-destructive border-destructive/30"
                    onClick={() => {
                      stopAudioPlayback();
                      setCurrentPlayingMessageId(null);
                    }}
                  >
                    <AlertCircle className="mr-1 h-3.5 w-3.5" />
                    <span>재생 중지</span>
                  </Button>
                )}
              </div>
            </div>
          )}

          <ChatInput
            onSend={handleSendMessage}
            disabled={isGenerating || isInitialGreetingGenerating}
            placeholder={isGenerating ? "메시지가 오고 있어요..." : "메시지를 입력하세요..."}
          />
        </div>
      </div>

      {/* No Credits Alert Dialog */}
      <CreditAlertDialog
        open={showCreditAlert}
        onOpenChange={setShowCreditAlert}
        showPricingCards={true}
      />

      {/* Mobile Navigation - z-index higher than the message input area */}
      <div className="relative z-30 sm:hidden">
        <MobileNav />
      </div>

      {/* 채팅 초기화 확인 대화상자 */}
      <AlertDialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>채팅 초기화</AlertDialogTitle>
            <AlertDialogDescription>
              채팅 내역을 모두 초기화하시겠습니까? 이 작업은 쉧을 수 없습니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                // 채팅 내역 초기화
                setMessages([]);
                localStorage.removeItem(`chat-${username}`);
                toast({
                  title: "채팅 초기화",
                  description: "모든 채팅 내역이 삭제되었습니다.",
                });

                // 수동 초기화 진행 중 플래그 설정 - useEffect에서 자동 인사말 생성 방지
                manualResetInProgressRef.current = true;

                // 참조 초기화 - 새로운 인사말 생성을 위해 참조 해제
                if (greetingGeneratedRef && greetingGeneratedRef.current) {
                  greetingGeneratedRef.current = false;
                }

                // 초기화 후 자동으로 첫 인사말 생성
                // 인사말 생성 중임을 표시
                setIsInitialGreetingGenerating(true);

                // 다음 렌더링 사이클에 인사말 생성 함수 호출
                setTimeout(() => {
                  // 타임아웃을 사용하여 렌더링 사이클 이후에 작동하도록 설정
                  const latestPosts = initialProfile?.latestPosts || [];
                  const firstPostImage = latestPosts?.[0]?.displayUrl;

                  // 사용자 페르소나 정보 가져오기
                  const userPersona = session?.user?.user_metadata?.persona || '';

                  // 인사말 생성 완료 후 상태 반영
                  generateGreetingFromImage(
                    username,
                    firstPostImage || "",
                    initialProfile,
                    userPersona // 사용자 페르소나 전달
                  )
                    .then((greetingText) => {
                      const newMessage: ChatMessage = {
                        id: crypto.randomUUID(),
                        role: MessageRole.Bot,
                        content: greetingText,
                        timestamp: new Date().toISOString(),
                        imageUrl: firstPostImage,
                      };

                      setMessages([newMessage]);
                      setIsInitialGreetingGenerating(false);
                      // 수동 초기화 완료 후 플래그 초기화
                      manualResetInProgressRef.current = false;
                    })
                    .catch(() => {
                      setIsInitialGreetingGenerating(false);
                      // 오류 발생 시에도 플래그 초기화
                      manualResetInProgressRef.current = false;
                    });
                }, 100);
              }}
            >
              초기화
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}